import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomFieldsOptionsListDTO, CustomPageType, IMeetingPresenterGroupDTO } from '../../../../../../../api/custom-pages.dao';
import {
    EMBEDDED_HTML_FILE_OBJECT_KEY,
    EMBEDDED_WEBPAGE_URL,
    EMBEDDED_ZIP_FILE,
    EMBED_CODE_ID,
    ENABLED,
    EXTERNAL_LINK_URL,
    LABEL,
    LINK_DESCRIPTION,
    PAGE_LINK_ENABLED,
    PATH,
    PUBLIC_ENABLED,
    RICH_TEXT,
    MEETING_ID,
    USER_SHOWCASE,
    RESTRICT_ACCESS_ENABLED,
    PRESENTER_GROUPS_ENABLED,
} from '../../../../../../../modules/passport-pages/config';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { CommonModal } from '../../../../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import Text from '../../../../../../global/text/text';
import { CustomPageFormViewModel } from './custom-page.viewmodel';
import './custom-page.presentation.scss';
import UploadButton, { FILE_TYPE } from '../../../../shared/upload-button';
import Spinner from '../../../../../../global/spinner/spinner';
import { RestrictAccessContainer } from '../default-pages/restrict-access-container';
import { notPublicPages } from './custom-pages.viewmodel';
import { ADD_TEXT, APPLY_AND_CLOSE_TEXT, APPLY_TEXT } from '../../../../../../../constants/common-labels.constants';
import { KnovioLabel } from '../../../../../../../api/connections.dao';
import { PresenterGroupsContainer } from './presenter-groups-container';

export type Props = {
    types: CustomPageType[];
    pageVm: CustomPageFormViewModel;
    onCancel: () => void;
    onApply: () => Promise<void>;
    onAdd: () => Promise<void>;
    onApplyAndClose: () => Promise<void>;
    onDelete?: () => Promise<void>;
    isEditPage: boolean;
    organizationTypes?: string[];
    customFieldsOptionsList?: CustomFieldsOptionsListDTO[];
    meetingPresenterGroups?: IMeetingPresenterGroupDTO[];
};

export const CustomPagePresentation = observer((props: Props): JSX.Element => {
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const { formatMessage } = useIntl();

    const { pageVm } = props;

    const confirmDeletion = () => setShowConfirmation(true);
    const closeConfirmation = () => setShowConfirmation(false);

    const onTypeChange = e => {
        if (!props.isEditPage) pageVm.changeType(e.target.value as CustomPageType);
    };

    const pageLabel = pageVm.getFieldByKey(LABEL);
    const pagePath = pageVm.getFieldByKey(PATH);
    const pageEnabled = pageVm.getFieldByKey(ENABLED);
    const pagePublicEnabled = pageVm.getFieldByKey(PUBLIC_ENABLED);
    const pageLandingPage = pageVm.getProps().landingPage;
    const pageLinkEnabled = pageVm.getFieldByKey(PAGE_LINK_ENABLED);
    const linkDescription = pageVm.getFieldByKey(LINK_DESCRIPTION);
    const restrictAcceddEnabled = pageVm.getFieldByKey(RESTRICT_ACCESS_ENABLED);
    const presenterGroupsEnabled = pageVm.getFieldByKey(PRESENTER_GROUPS_ENABLED);

    const shouldBlockApply = () => !pageVm.ensurePresenterGroupsSelected() || !pageVm.isPageValid();

    const applyAndClose = () => shouldBlockApply()
        ? pageVm.setVmTouched()
        : props.onApplyAndClose();
    const apply = () => shouldBlockApply()
        ? pageVm.setVmTouched()
        : props.onApply();
    const onAdd = () => !pageVm.isPageValid() ? pageVm.setVmTouched() : props.onAdd();

    const shouldShowPublicToggle = !notPublicPages.includes(pageVm.getType());

    return (
        <CommonPopupBodyFooterLayout
            className='custom-page-modal'
            body={
                <form className='oe-form w-80' style={{ margin: 'auto' }}>
                    <div className='m-bottom-25px'>
                        <div className='oe-form__row flex-content-space-between'>
                            <FormField label={formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor)}
                                className='flex-grow-0 w-40'>
                                <input
                                    className='oe-form-field__control'
                                    onBlur={pageVm.setValueTouched.bind(pageVm, LABEL)}
                                    value={pageLabel.value as string}
                                    type='text'
                                    onChange={e => pageVm.setFieldValueByKey(LABEL, e.target.value)}
                                />
                                <span className='oe-form-field__error'>{
                                    !pageLabel.valid && pageLabel.touched &&
                                    <FormattedMessage
                                        values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor) }}
                                        {...pageLabel.validationMessage}
                                    />
                                }</span>
                            </FormField>
                            <FormField
                                label={formatMessage({ id: 'oe.customPage.pageType', defaultMessage: 'Page Type' })}
                                className='flex-grow-0 w-40'>
                                <select
                                    value={pageVm.getType()}
                                    className='oe-form-field__control'
                                    disabled={props.isEditPage}
                                    onChange={onTypeChange}
                                >
                                    {props.types.map(type => (
                                        <option key={type} value={type} className='oe-form-field__control'>
                                            {type === CustomPageType.UserShowcase ? 'Showcase' :
                                              type === CustomPageType.Knovio ? KnovioLabel.OEStream :
                                              type === CustomPageType.BookAMeeting ? 'Book a Meeting' : type}
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                        </div>
                        <div className='oe-form__row flex-content-space-between'>
                            <FormField className='flex-grow-0 w-40 m-bottom-0px'>
                                <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                                    <OEToggle
                                        isDisabled={pageLandingPage}
                                        isSelected={pageEnabled.value as boolean}
                                        onClick={pageVm.setFieldValueByKey.bind(pageVm, ENABLED, !pageEnabled.value)}
                                    />
                                    {pageEnabled.value as boolean
                                        ? <Text message='Page (after sign in) is enabled' id='oe.customPage.enabled' description='Label text' />
                                        : <Text message='Page (after sign in) is disabled' id='oe.customPage.disabled' description='Label text' />
                                    }
                                </div>
                                {shouldShowPublicToggle &&
                                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                                        <OEToggle
                                            isSelected={pagePublicEnabled.value as boolean}
                                            onClick={pageVm.setFieldValueByKey.bind(pageVm, PUBLIC_ENABLED, !pagePublicEnabled.value)}
                                        />
                                        {pagePublicEnabled.value as boolean
                                            ? <Text message='Page (public) is enabled' id='oe.customPage.publicEnabled' description='Label text' />
                                            : <Text message='Page (public) is disabled' id='oe.customPage.publicDisabled' description='Label text' />
                                        }
                                    </div>
                                }
                            </FormField>
                            <div className='oe-form-field flex-grow-0 w-40 m-bottom-0px '>
                                <FormField>
                                    <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                                        <OEToggle
                                            isDisabled={!pageEnabled.value}
                                            isSelected={pageLinkEnabled.value as boolean}
                                            onClick={pageVm.setFieldValueByKey.bind(pageVm, PAGE_LINK_ENABLED, !pageLinkEnabled.value)}
                                        />
                                        <Text
                                            message='Show link on Welcome page'
                                            id='oe.defaultPages.showLinkOnWelcomePage'
                                            description='Label text'
                                        />
                                    </div>
                                </FormField>
                                <FormField
                                    className='m-bottom-0px'
                                    label={formatMessage(pageVm.getFieldConfigByKey(LINK_DESCRIPTION).labelDescriptor, { prefix: pageLabel.value })}
                                >
                                    <div className='flex'>
                                        <input
                                            className='oe-form-field__control'
                                            onBlur={pageVm.setValueTouched.bind(pageVm, LINK_DESCRIPTION)}
                                            value={linkDescription.value as string || ''}
                                            type='text'
                                            onChange={e => pageVm.setFieldValueByKey(LINK_DESCRIPTION, e.target.value)}
                                        />
                                    </div>
                                    <span className='oe-form-field__error'>{
                                        !linkDescription.valid && linkDescription.touched &&
                                        <FormattedMessage
                                            values={{
                                                fieldName: formatMessage(pageVm.getFieldConfigByKey(LABEL).labelDescriptor,
                                                    { prefix: pageLabel.value })
                                            }}
                                            {...linkDescription.validationMessage}
                                        />
                                    }</span>
                                </FormField>

                            </div>

                        </div>
                        <div className='oe-form__row flex-content-space-between'>
                            <FormField label={formatMessage(pageVm.getFieldConfigByKey(PATH).labelDescriptor)}
                                className='flex-grow-0 w-40'>
                                <input
                                    className='oe-form-field__control'
                                    onBlur={pageVm.setValueTouched.bind(pageVm, PATH)}
                                    value={pagePath.value as string}
                                    type='text'
                                    onChange={e => pageVm.setFieldValueByKey(PATH, e.target.value)} />
                                <span className='oe-form-field__error' style={{ whiteSpace: 'pre-line' }}>
                                    {!pagePath.valid && pagePath.touched &&
                                        <FormattedMessage
                                            values={{
                                                fieldName: formatMessage(pageVm.getFieldConfigByKey(PATH).labelDescriptor),
                                                pagePath: `"${pagePath.value as string}"`
                                            }}
                                            {...pageVm.getFieldByKey(PATH).validationMessage} />
                                    }
                                </span>
                            </FormField>
                            {
                                pageVm.getType() === CustomPageType.Knovio &&
                                <FormField
                                    label={formatMessage(pageVm.getFieldConfigByKey(EMBED_CODE_ID).labelDescriptor)}
                                    className='flex-grow-0 w-40'>
                                    <input
                                        className='oe-form-field__control'
                                        onBlur={pageVm.setValueTouched.bind(pageVm, EMBED_CODE_ID)}
                                        value={pageVm.getFieldByKey(EMBED_CODE_ID).value as string}
                                        type='text'
                                        onChange={e => pageVm.setFieldValueByKey(EMBED_CODE_ID, e.target.value)}
                                    />
                                    <span className='oe-form-field__error'>
                                        {
                                            !pageVm.getFieldByKey(EMBED_CODE_ID).valid && pageVm.getFieldByKey(EMBED_CODE_ID).touched &&
                                            <FormattedMessage
                                                values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(EMBED_CODE_ID).labelDescriptor) }}
                                                {...pageVm.getFieldByKey(EMBED_CODE_ID).validationMessage}
                                            />
                                        }</span>
                                </FormField>
                            }
                            {
                                pageVm.getType() === CustomPageType.EmbeddedWebpage &&
                                <FormField
                                    label={formatMessage(pageVm.getFieldConfigByKey(EMBEDDED_WEBPAGE_URL).labelDescriptor)}
                                    className='flex-grow-0 w-40'>
                                    <input
                                        className='oe-form-field__control'
                                        onBlur={pageVm.setValueTouched.bind(pageVm, EMBEDDED_WEBPAGE_URL)}
                                        value={pageVm.getFieldByKey(EMBEDDED_WEBPAGE_URL).value as string}
                                        type='text'
                                        onChange={e => pageVm.setFieldValueByKey(EMBEDDED_WEBPAGE_URL, e.target.value)}
                                    />
                                    <span className='oe-form-field__error'>
                                        {
                                            !pageVm.getFieldByKey(EMBEDDED_WEBPAGE_URL).valid && pageVm.getFieldByKey(EMBEDDED_WEBPAGE_URL).touched &&
                                            <FormattedMessage
                                                values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(EMBEDDED_WEBPAGE_URL).labelDescriptor) }}
                                                {...pageVm.getFieldByKey(EMBEDDED_WEBPAGE_URL).validationMessage}
                                            />
                                        }</span>
                                </FormField>
                            }
                            {
                                pageVm.getType() === CustomPageType.UserShowcase &&
                                <FormField
                                    label={formatMessage(pageVm.getFieldConfigByKey(USER_SHOWCASE).labelDescriptor)}
                                    className='flex-grow-0 w-40'>
                                    <input
                                        className='oe-form-field__control'
                                        onBlur={pageVm.setValueTouched.bind(pageVm, EMBED_CODE_ID)}
                                        value={pageVm.getFieldByKey(EMBED_CODE_ID).value as string}
                                        type='text'
                                        onChange={e => pageVm.setFieldValueByKey(EMBED_CODE_ID, e.target.value)}
                                    />
                                    <span className='oe-form-field__error'>
                                        {!pageVm.getFieldByKey(EMBED_CODE_ID).valid && pageVm.getFieldByKey(EMBED_CODE_ID).touched &&
                                            <FormattedMessage
                                                values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(USER_SHOWCASE).labelDescriptor) }}
                                                {...pageVm.getFieldByKey(USER_SHOWCASE).validationMessage}
                                            />}
                                    </span>
                                </FormField>
                            }

                            {
                                pageVm.getType() === CustomPageType.ExternalLink &&
                                <FormField
                                    label={formatMessage(pageVm.getFieldConfigByKey(EXTERNAL_LINK_URL).labelDescriptor)}
                                    className='flex-grow-0 w-40'>
                                    <input
                                        className='oe-form-field__control'
                                        onBlur={pageVm.setValueTouched.bind(pageVm, EXTERNAL_LINK_URL)}
                                        value={pageVm.getFieldByKey(EXTERNAL_LINK_URL).value as string}
                                        type='text'
                                        onChange={e => pageVm.setFieldValueByKey(EXTERNAL_LINK_URL, e.target.value)}
                                    />
                                    <span className='oe-form-field__error'>
                                        {
                                            !pageVm.getFieldByKey(EXTERNAL_LINK_URL).valid && pageVm.getFieldByKey(EXTERNAL_LINK_URL).touched &&
                                            <FormattedMessage
                                                values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(EXTERNAL_LINK_URL).labelDescriptor) }}
                                                {...pageVm.getFieldByKey(EXTERNAL_LINK_URL).validationMessage}
                                            />
                                        }</span>
                                </FormField>
                            }
                            {
                                pageVm.getType() === CustomPageType.SingleMeeting &&
                                <FormField
                                    label={formatMessage(pageVm.getFieldConfigByKey(MEETING_ID).labelDescriptor)}
                                    className='flex-grow-0 w-40'
                                >
                                    <input
                                        className='oe-form-field__control'
                                        onBlur={pageVm.setValueTouched.bind(pageVm, MEETING_ID)}
                                        value={pageVm.getFieldByKey(MEETING_ID).value as string}
                                        type='number'
                                        onChange={e => pageVm.setFieldValueByKey(MEETING_ID, e.target.value)}
                                    />
                                    <span className='oe-form-field__error'>
                                        {!pageVm.getFieldByKey(MEETING_ID).valid && pageVm.getFieldByKey(MEETING_ID).touched &&
                                            <FormattedMessage
                                                values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(MEETING_ID).labelDescriptor) }}
                                                {...pageVm.getFieldByKey(MEETING_ID).validationMessage}
                                            />}
                                    </span>
                                </FormField>
                            }
                        </div>
                        {
                            pageVm.getType() === CustomPageType.RichText &&
                            <FormField
                                className='m-bottom-5px'
                                label={formatMessage(pageVm.getFieldConfigByKey(RICH_TEXT).labelDescriptor)}
                                labelFor='rich-text-page-type'
                            >
                                <RichTextEditor
                                    className='rich-text-page-type'
                                    id='rich-text-page-type'
                                    onBlur={pageVm.setValueTouched.bind(pageVm, RICH_TEXT)}
                                    value={pageVm.getFieldByKey(RICH_TEXT).value as string || ''}
                                    onChange={(event, editor) => {
                                        pageVm.setFieldValueByKey(RICH_TEXT, editor.getData());
                                    }}
                                    validationError={!pageVm.getFieldByKey(RICH_TEXT).valid && pageVm.getFieldByKey(RICH_TEXT).touched &&
                                        <FormattedMessage
                                            values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(RICH_TEXT).labelDescriptor) }}
                                            {...pageVm.getFieldByKey(RICH_TEXT).validationMessage}
                                        />}
                                />
                            </FormField>
                        }
                        {
                            pageVm.getType() === CustomPageType.EmbeddedHTML &&
                            <FormField>
                                {pageVm.isFileUploading ?
                                    <Spinner /> :
                                    <>
                                        <UploadButton
                                            fileName={(pageVm.getFieldByKey(EMBEDDED_ZIP_FILE).value as File).name || ''}
                                            onChange={pageVm.setEmbeddedHTMLFile}
                                            uploadedFileType={FILE_TYPE.ZIP}
                                            additionalText={<FormattedMessage
                                                description='Label text'
                                                defaultMessage='. Zip file must contain an index.html file.'
                                                id='oe.uploadedFileType.mustContain'
                                            />}
                                        />
                                        <span className='oe-form-field__error'>
                                            {
                                                (
                                                    (!pageVm.getFieldByKey(EMBEDDED_ZIP_FILE).valid && pageVm.getFieldByKey(EMBEDDED_ZIP_FILE).touched) ||
                                                    (
                                                        !pageVm.getFieldByKey(EMBEDDED_HTML_FILE_OBJECT_KEY).valid &&
                                                        pageVm.getFieldByKey(EMBEDDED_HTML_FILE_OBJECT_KEY).touched
                                                    )
                                                ) &&
                                                <FormattedMessage
                                                    values={{ fieldName: formatMessage(pageVm.getFieldConfigByKey(EMBEDDED_ZIP_FILE).labelDescriptor) }}
                                                    {...pageVm.getFieldByKey(EMBEDDED_ZIP_FILE).validationMessage}
                                                />
                                            }
                                        </span>
                                    </>}
                            </FormField>
                        }

                        <FormField
                            className='m-top-40px'
                            data-test-id='restrict-access-toggle'
                        >
                            <div className='website-custom__checkbox-label m-bottom-0px'>
                                <OEToggle
                                    isSelected={restrictAcceddEnabled.value as boolean}
                                    onClick={() => {
                                        pageVm.setFieldValueByKey(RESTRICT_ACCESS_ENABLED, !restrictAcceddEnabled.value);
                                    }}
                                />
                                {restrictAcceddEnabled.value ?
                                    <Text
                                        message={`Access to ${pageLabel.value} page is restricted`}
                                        id={`oe.customPage.accessTo${pageLabel.value}Restricted`}
                                        description='Label text'
                                    /> :
                                    <Text
                                        message={`Access to ${pageLabel.value} page is not restricted`}
                                        id={`oe.customPage.accessTo${pageLabel.value}NotRestricted`}
                                        description='Label text'
                                    />
                                }
                            </div>
                            <div className={'font-size-12px line-height-21px m-top-15px'}>
                                <Text message={`When access to ${pageLabel.value} page is restricted, attendees will only have access
                                    to the ${pageLabel.value} page if they match all the criteria of at least one filter.`}
                                    id={`oe.defaultPage.accessTo${pageLabel.value}Description`}
                                    description='Label text'
                                />
                            </div>
                        </FormField>
                        {restrictAcceddEnabled.value
                            ? <RestrictAccessContainer
                                pageVm={pageVm}
                                organizationTypes={props.organizationTypes}
                                maxFiltersCount={20}
                                customFieldsOptionsList={props.customFieldsOptionsList}
                            />
                            : null
                        }

                        {pageVm.getType() === CustomPageType.BookAMeeting &&
                            <FormField
                                className='m-top-40px'
                                data-test-id='presenter-groups-toggle'
                            >
                                <div className='website-custom__checkbox-label m-bottom-0px'>
                                    <OEToggle
                                        isSelected={presenterGroupsEnabled.value as boolean}
                                        onClick={() => {
                                            pageVm.setFieldValueByKey(PRESENTER_GROUPS_ENABLED, !presenterGroupsEnabled.value);
                                        }}
                                    />
                                    <Text
                                        message='Select Presenter Groups'
                                        id='oe.customPage.selectPresenterGroups'
                                        description='Label text'
                                    />
                                </div>
                                <div className={'font-size-12px line-height-21px m-top-15px'}>
                                    <Text message={`Here you can specify which presenter group's meetings you want to display on the page.
                                        If disabled, all presenter groups assigned to bookable meetings will be displayed.`}
                                        id='oe.customPage.presenterGroupsText'
                                        description='Label text'
                                    />
                                </div>
                            </FormField>
                        }
                        {presenterGroupsEnabled.value
                            ? <PresenterGroupsContainer
                                pageVm={pageVm}
                                meetingPresenterGroups={props.meetingPresenterGroups}
                                maxFiltersCount={20}
                            />
                            : null
                        }

                        {
                            props.isEditPage &&
                            <DefaultButton
                                modifier={['fat', 'confirm-warn']}
                                text={
                                    <FormattedMessage id='oe.customPage.delete'
                                        defaultMessage='Delete This Custom Page'
                                        description='Label text'
                                    />
                                }
                                onClick={confirmDeletion}
                                className='m-top-40px'
                            />
                        }
                        {
                            props.isEditPage &&
                            showConfirmation &&
                            <CommonModal
                                isOpen={showConfirmation}
                                onRequestClose={closeConfirmation}
                                footer={
                                    <div className='buttons-block buttons-block--from-right'>
                                        <DefaultButton
                                            modifier={['fat', 'confirm-warn']}
                                            text={
                                                <FormattedMessage
                                                    id='oe.common.deleteCustomPage'
                                                    defaultMessage='Delete Custom Page'
                                                    description='Label text'
                                                />
                                            }
                                            onClick={props.onDelete}
                                        />
                                        <DefaultCloseModalButton />
                                    </div>
                                }
                            >
                                <div className='alert-body'>
                                    <span className='alert-message'>
                                        <FormattedMessage
                                            id='oe.customPage.confirmDeleteMessage'
                                            defaultMessage='Are you sure you want to delete this Custom Page ?'
                                            description='Label text'
                                        />
                                    </span>
                                </div>
                            </CommonModal>
                        }
                    </div>

                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        text={formatMessage(props.isEditPage ? APPLY_AND_CLOSE_TEXT : ADD_TEXT)}
                        modifier={['fat', 'confirm-safe']}
                        disabled={pageVm.isFileUploading}
                        onClick={props.isEditPage ? applyAndClose : onAdd}
                    />
                    {
                        props.isEditPage &&
                        <DefaultButton
                            text={formatMessage(APPLY_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            disabled={pageVm.isFileUploading}
                            onClick={apply}
                        />
                    }
                    <DefaultCloseSubWindowButton />
                </div>
            }
        />
    );
});
